import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { CustomerModel } from '@df/session/api/customer.model';
import { Nexus } from '@tomandco/nexus-api-types';
import find from 'lodash-es/find';
import { AddressService } from './address.service';

export interface IAddressBasicData {
    firstname?: string;
    lastname?: string;
    city?: string;
    company?: string;
    postcode?: string;
    street_1?: string;
    street_2?: string;
    street_3?: string;
    telephone?: string;
    telephone2?: string;
    country_id?: string;
    country?: string;
    region_id?: number | string; // its magento/Nexus id of region, not a code
    region?: string;
    prefix?: string;
    nickname?: string;
    /**
     * Customer address id used with address
     * @type {number}
     */
    customer_address_id?: number;
    /**
     * Flag if address is valid
     * @type {boolean}
     */
    valid?: boolean;
}

export const ADDRESS_FIELDS = [
    'firstname',
    'lastname',
    'city',
    'company',
    'postcode',
    'street_1',
    'street_2',
    'street_3',
    'telephone',
    'telephone2',
    'country_id',
    'country',
    'region_id',
    'region'
];
export interface IAddressData extends IAddressBasicData {
    id?: number;
    is_billing?: boolean;
    is_shipping?: boolean;
}

export class Address {
    @NgInject(RequestService)
    protected request!: RequestService;

    @NgInject(App)
    protected app!: App;

    @NgInject(AddressService)
    addressService!: AddressService;

    constructor(public _data: IAddressData) {}

    /**
     * Data setter / getter
     */
    get data(): IAddressData {
        return this._data;
    }

    set(data: IAddressData) {
        this._data = data;
    }

    /**
     * Id getter
     */

    get id(): number | undefined {
        return this._data.id;
    }
    get is_billing(): boolean {
        return this._data.is_billing || false;
    }
    get is_shipping(): boolean {
        return this._data.is_shipping || false;
    }

    /**
     * Sends save customer address data request
     */
    save(status: Status, onSuccess: IRequestCallback, onError: IRequestCallback): Promise<CustomerModel> {
        return this.request.send<CustomerModel>(status, () => this.request.nexus.customer.saveAddress(this.data), onSuccess, onError);
    }

    /**
     * Sends save customer address data request
     */
    formSave(form, status: Status, onSuccess: IRequestCallback, onError: IRequestCallback): Promise<CustomerModel> | void {
        return this.request.submitForm<CustomerModel>(
            form,
            status,
            () => this.request.nexus.customer.saveAddress(this.data),
            onSuccess,
            onError
        );
    }

    /**
     * Sends address removal request
     */
    remove(status: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback): Promise<CustomerModel> {
        const id = this.data.id;
        if (!id) {
            throw new Error('Address id is not set');
        }
        return this.request.send<CustomerModel>(status, () => this.request.nexus.customer.removeAddress({ id }), onSuccess, onError);
    }

    get oneLine(): string {
        return this.format('oneLine');
    }

    get short(): string {
        return this.format('short');
    }

    format(pattern: string): string {
        const data = this._data;
        switch (pattern) {
            case 'oneLine':
                return [
                    `${data.firstname || ''} ${data.lastname || ''}`.trim(),
                    [data.street_1, data.street_2, data.street_3].join(' ').trim(),
                    `${data.city || ''} ${data.postcode || ''}`.trim(),
                    data.telephone ? 'T:\u00A0' + data.telephone : '',
                    data.telephone2 ? 'T2:\u00A0' + data.telephone2 : ''
                ]
                    .filter(i => !!i)
                    .join(', ');
            case 'short':
                return [
                    data.firstname,
                    ' ',
                    data.lastname,
                    ', ',
                    data.street_1,
                    ' ',
                    data.street_2,
                    ' ',
                    data.street_3,
                    ', ',
                    data.city,
                    ' ',
                    data.postcode
                ].join('');
        }
        return '';
    }

    /**
     * Checks if addresss is same as another addresss
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @param  {Address} address Address to compare with
     */
    isSame(address: Address): boolean {
        if (!address) {
            return false;
        }
        return this.addressService.areSame(this.data, address.data);
    }

    /**
     * Gets country model
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {Nexus.Lib.General.Country}
     */
    getCountry(): Nexus.Lib.General.Country | undefined {
        return find(
            this.app.apiConfigService.countries ? this.app.apiConfigService.countries || [] : [],
            country => country.code === this.data.country_id
        );
    }

    /**
     * Gets country name
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {string}
     */
    get countryName(): string | undefined {
        const country = this.getCountry();
        return country ? country.name : undefined;
    }

    /**
     * Gets region model
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {Nexus.Lib.General.Region}
     */
    getRegion(): Nexus.Lib.General.Region | undefined {
        if (this.data.region_id) {
            const country = this.getCountry();
            if (country && country.regions) {
                return find(country.regions, region => region.id === this.data.region_id);
            }
        }
        return undefined;
    }

    /**
     * Gets region name
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     * @return {string}
     */
    get regionName(): string | undefined {
        if (this.data.region) {
            return this.data.region;
        }
        const region = this.getRegion();
        if (region) {
            return region.name;
        }
        return undefined;
    }
}
